/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, ViewEncapsulation, Output, EventEmitter, OnInit, Input, OnDestroy, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { ContentActionRef, ExtensionService, NavigationState, ProfileState } from '@alfresco/adf-extensions';
import { AppStore, getHeaderColor, getAppName, getLogoPath, getHeaderImagePath, getHeaderTextColor, getNavigationState, getUserProfile } from '@alfresco/aca-shared/store';
import { AppExtensionService } from '@alfresco/aca-shared';
import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { APP_CONFIG_PLUGINS, APP_CONFIG_WIDGETS_IMAGE_ICON, BUILDER_INTEGRATION_SERVICE, CONTENT_SERVICE_PLUGIN, EFLOW_INTEGRATION_SERVICE, SIGN_INTEGRATION_SERVICE, SIGNAFORM_INTEGRATION_SERVICE } from '../../models/alden-constants';
import { AldenService } from '../../services/alden.service';
import { isPluginEnabled, hasPluginAccess } from '../../utils/plugin-utils';
import { isAlden } from '../../rules/alden.rules';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'app-header' }
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  private onDestroy$: Subject<boolean> = new Subject<boolean>();
  @Output()
  toggleClicked = new EventEmitter();

  @Input() expandedSidenav = true;

  appName$: Observable<string>;
  headerColor$: Observable<any>;
  headerTextColor$: Observable<string>;
  logo$: Observable<string>;
  landingPage: string;
  showTitleHeader: boolean;
  showGlobalSearch: boolean;
  navigation: NavigationState;

  profile$: Observable<ProfileState>;

  actions: Array<ContentActionRef> = [];

  actualUrl: string;
  isViewer: boolean;

  plugins: { [pluginId: string]: boolean | string };
  contentServiceEnabled: boolean;
  eflowEnabled: boolean;
  signaformEnabled: boolean;
  builderEnabled: boolean;
  signEnabled: boolean;

  widgetsImageIcon: boolean;

  constructor(protected store: Store<AppStore>, private appExtensions: AppExtensionService, protected extensions: ExtensionService, 
    private router: Router, private injector: Injector, public aldenService: AldenService
  ) {
    this.headerColor$ = store.select(getHeaderColor);
    this.headerTextColor$ = store.select(getHeaderTextColor);
    this.appName$ = store.select(getAppName);
    this.logo$ = store.select(getLogoPath);
    this.landingPage = this.aldenService.getAppConfigProp('landingPage', '/libraries');
    this.showTitleHeader = this.aldenService.getAppConfigProp('showTitleHeader', false);
    this.showGlobalSearch = this.aldenService.getAppConfigProp('showGlobalSearch', true);
    this.plugins = this.aldenService.getAppConfigProp(APP_CONFIG_PLUGINS); 
    this.widgetsImageIcon = this.aldenService.getAppConfigProp(APP_CONFIG_WIDGETS_IMAGE_ICON);
    
    this.profile$ = this.store.select(getUserProfile);

    this.setPlugins();

    store.select(getNavigationState).subscribe((navigation) => {
      this.navigation = navigation;
      this.isInViewer();
    });

    store.select(getHeaderImagePath).subscribe((path) => {
      document.body.style.setProperty('--header-background-image', `url('${path}')`);
    });
  }

  async ngOnInit() {
    // Suscribirse a los eventos de navegación
    this.actualUrl = this.router.url;
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.actualUrl = event.urlAfterRedirects;
    });

    this.appExtensions
      .getHeaderActions()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((actions) => {
        this.actions = actions;
      });

    this.headerTextColor$.subscribe((color) => {
      document.documentElement.style.setProperty('--adf-header-text-color', color);
    });

    const ruleContext = await this.aldenService.getRuleContext(null);
    const expandedSidenav = isAlden(ruleContext, this.injector);
    if(this.expandedSidenav != expandedSidenav) {
      this.expandedSidenav = expandedSidenav;
      this.toggleClicked.emit();
    }
  }
  
  private setPlugins() {
    this.isContentServiceEnabled();
    this.setPluginsEnabled();    
  }

  private isContentServiceEnabled() {
    this.contentServiceEnabled = isPluginEnabled(this.plugins, CONTENT_SERVICE_PLUGIN);
  }

  private setPluginsEnabled() {
    this.profile$.subscribe((profile) => {
      if(profile && profile.id) {
          this.eflowEnabled = hasPluginAccess(profile, this.plugins, this.injector, EFLOW_INTEGRATION_SERVICE);
          this.signaformEnabled = hasPluginAccess(profile, this.plugins, this.injector, SIGNAFORM_INTEGRATION_SERVICE);
          this.builderEnabled = hasPluginAccess(profile, this.plugins, this.injector, BUILDER_INTEGRATION_SERVICE);
          this.signEnabled = hasPluginAccess(profile, this.plugins, this.injector, SIGN_INTEGRATION_SERVICE);
      }
    });
  }

  private isInViewer() {
    this.isViewer = this.navigation && this.navigation.url && this.navigation.url.includes('/viewer');
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  trackByActionId(_: number, action: ContentActionRef) {
    return action.id;
  }

  navigateTo(url: string) {
    this.router.navigate([url]);
  }
}
